import { useEffect, useMemo, useState } from 'react';
import { Alert, LinearProgress, Pagination, Paper, Snackbar, SnackbarCloseReason, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FerramentasDaListagemColhedor } from '../../../shared/components';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { useDebounce } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import { ColhedorService, IListagemColhedores } from '../../../shared/services/api/ColhedorService';

export const ListagemDeColhedores: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { debounce } = useDebounce();
  const navigate = useNavigate();
  
  const [rows, setRows] = useState<IListagemColhedores[]>([]);
  const [isLoading, setIsLoading] = useState(true); 
  const [totalCount, setTotalCount] = useState(0);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error'  | 'info'  | 'success'>('warning');  
    
  const busca = useMemo(() => {
    return searchParams.get('busca') || '';
  }, [searchParams]);

  const pagina = useMemo(() => { 
    return Number(searchParams.get('pagina') || '1');
  }, [searchParams]);

  const tipo = useMemo(() => {
    return searchParams.get('tipo') || 'c.nome'; 
  }, [searchParams]);
  
  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event, 
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
  };
    
  useEffect(() => {
    setIsLoading(true);
    debounce(() => {
      ColhedorService.getAll(pagina, tipo, busca)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setTotalCount(result.totalCount);
            setRows(result.data);
          }
        });
    });
  }, [busca, tipo, pagina]);
  
  return (
    <LayoutBaseDePagina
      titulo='Listagem de Colhedores'
      barraDeFerramentas={
        <FerramentasDaListagemColhedor
          mostrarInputBusca
          textoDaBusca={busca}
          textoBotaoNovo='Adicionar'
          textoDoSelect={tipo}
          aoClicarEmNovo={() => navigate('/colhedores/detalhe/novo')}
          aoMudarTextoDeBusca={texto => setSearchParams({ busca: texto, tipo: tipo,  pagina: '1' }, { replace: true })}
          aoMudarTextoDoSelect={textoselect => setSearchParams({ busca: busca,  tipo: textoselect, pagina: '1' }, { replace: true })}
          dadosSelecao={[
            { nameBD: 'c.nome', descricao: 'Nome' },
            { nameBD: 'c.numero', descricao: 'Numero' },
            { nameBD: 'c.cpf', descricao: 'CPF' },
          ]} 
        />
      }
    >
      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg}  autoHideDuration={900} onClose={handleCloseMsg}>
        <Alert
          onClose={handleCloseMsg}
          severity={tms}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {msg}
        </Alert>
      </Snackbar>
      <TableContainer component={Paper} variant="outlined" sx={{ m: 1, width: 'auto' }}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>CPF</TableCell>
              <TableCell>Fazenda</TableCell>
              <TableCell align='right'>Numero</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.guuidcolhedor} 
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/colhedores/detalhe/${row.guuidcolhedor}`)}
              >
                <TableCell>{row.nome}</TableCell>
                <TableCell align='left' >{row.cpf.length === 11 ? row.cpf.replace(/\D/g, '').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4'): 
                  row.cpf}</TableCell>
                <TableCell>{row.fazenda}</TableCell>
                <TableCell align='right'>{row.numero}</TableCell>
                <TableCell></TableCell>
              </TableRow>
            ))}
          </TableBody>
  
          {totalCount === 0 && !isLoading && (
            <caption>{Environment.LISTAGEM_VAZIA}</caption>
          )}
  
          <TableFooter>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={4}>
                  <LinearProgress variant='indeterminate' />
                </TableCell>
              </TableRow>
            )}
            {(totalCount > 0 && totalCount > 18) && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Pagination
                    page={pagina}
                    count={Math.ceil(totalCount / 18)}
                    onChange={(_, newPage) => setSearchParams({ busca, tipo: tipo,  pagina: newPage.toString() }, { replace: true })}
                  />
                </TableCell>
              </TableRow>
            )}
          </TableFooter>
        </Table>
      </TableContainer>
    </LayoutBaseDePagina>
  );
};